/* Tactile effects: particle bursts, touch ripples and haptics.
 * All effects render in a fixed overlay, so they never touch the layout of
 * the element that triggered them.
 */
import { haptic, motionLevel } from './living';

let layer = null;
function fxLayer() {
    if (layer && document.body.contains(layer)) return layer;
    layer = document.createElement('div');
    layer.className = 'fx-layer';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    return layer;
}

const BURSTS = {
    water: { items: ['💧', '💧', '💦'], count: 10, spread: 70, lift: 90, gravity: 160, size: [12, 20] },
    confetti: { colors: ['var(--brand-green)', 'var(--brand-ochre)', 'var(--brand-terracotta)', 'var(--brand-sage)', '#f9c9d4'], count: 18, spread: 110, lift: 120, gravity: 220, size: [6, 10] },
    leaves: { items: ['🍃', '🌿', '🌸', '✨'], count: 12, spread: 120, lift: 130, gravity: 120, size: [12, 20] },
    sparkle: { items: ['✨', '⭐', '✨'], count: 9, spread: 80, lift: 80, gravity: 60, size: [10, 18] },
};

/** Explode a little celebration out of an element (or {x, y} point). */
export function burst(origin, type = 'confetti') {
    if (motionLevel() === 'off' || !origin) return;
    const cfg = BURSTS[type] || BURSTS.confetti;
    let x;
    let y;
    if (origin.getBoundingClientRect) {
        const r = origin.getBoundingClientRect();
        x = r.left + r.width / 2;
        y = r.top + r.height / 2;
    } else {
        ({ x, y } = origin);
    }
    const root = fxLayer();
    const count = motionLevel() === 'calm' ? Math.ceil(cfg.count / 2) : cfg.count;
    for (let i = 0; i < count; i++) {
        const el = document.createElement('span');
        el.className = 'fx-bit';
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.6;
        const dist = cfg.spread * (0.5 + Math.random() * 0.6);
        const size = cfg.size[0] + Math.random() * (cfg.size[1] - cfg.size[0]);
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        el.style.setProperty('--dy', `${Math.sin(angle) * dist * 0.6 - cfg.lift * (0.6 + Math.random() * 0.6)}px`);
        el.style.setProperty('--fall', `${cfg.gravity}px`);
        el.style.setProperty('--rot', `${(Math.random() - 0.5) * 720}deg`);
        el.style.animationDuration = `${800 + Math.random() * 500}ms`;
        if (cfg.items) {
            el.textContent = cfg.items[(Math.random() * cfg.items.length) | 0];
            el.style.fontSize = `${size}px`;
        } else {
            el.classList.add('fx-bit--confetti');
            el.style.width = `${size}px`;
            el.style.height = `${size * 0.45}px`;
            el.style.background = cfg.colors[(Math.random() * cfg.colors.length) | 0];
        }
        el.addEventListener('animationend', () => el.remove(), { once: true });
        root.appendChild(el);
    }
    haptic(type === 'confetti' ? [10, 40, 16] : 12);
}

/** Ink ripple clipped to the pressed element's shape. */
function ripple(target, e) {
    const r = target.getBoundingClientRect();
    if (r.width < 8 || r.height < 8 || r.width * r.height > 500000) return;
    const cs = getComputedStyle(target);
    const clip = document.createElement('span');
    clip.className = 'fx-ripple-clip';
    Object.assign(clip.style, {
        left: `${r.left}px`,
        top: `${r.top}px`,
        width: `${r.width}px`,
        height: `${r.height}px`,
        borderRadius: cs.borderRadius,
    });
    const dot = document.createElement('span');
    dot.className = 'fx-ripple';
    const size = Math.hypot(r.width, r.height) * 2;
    Object.assign(dot.style, {
        width: `${size}px`,
        height: `${size}px`,
        left: `${e.clientX - r.left - size / 2}px`,
        top: `${e.clientY - r.top - size / 2}px`,
    });
    clip.appendChild(dot);
    fxLayer().appendChild(clip);
    dot.addEventListener('animationend', () => clip.remove(), { once: true });
}

let installed = false;
/** One delegated listener gives every button/link in the app a ripple + tick.
 * It listens for `click`, not `pointerdown`: the browser only sends click for
 * a real tap, so a scroll that happens to start on a button no longer ripples
 * and vibrates as if it had been pressed. */
export function installTouchFx() {
    if (installed || typeof document === 'undefined') return;
    installed = true;
    document.addEventListener('click', (e) => {
        if (e.button !== 0 || motionLevel() === 'off') return;
        const target = e.target.closest?.('button, a[href], [role="button"], .tilt');
        if (!target || target.disabled || target.closest('[data-no-fx]')) return;
        ripple(target, e);
        if (e.pointerType === 'touch') haptic(6);
    }, { passive: true, capture: true });
}
